import { Injectable, Pipe, PipeTransform } from '@angular/core';
import {
  BackendErrorCode,
  NHS_ERROR_MESSAGES,
  NHS_ERROR_TITLES,
  NhsErrorCode,
} from '../models/error.model';

export interface NhsErrorCopy {
  title: string;
  message: string;
}

/**
 * Maps NHS/backend error codes to hardcoded English copy (fallback when i18n keys are not used).
 *
 * Usage in templates: `{{ code | nhsErrorMessage }}`
 * Usage in TS: `resolveFromCode(code)` for `{ title, message }`.
 */
@Injectable({ providedIn: 'root' })
@Pipe({
  name: 'nhsErrorMessage',
  standalone: true,
})
export class NhsErrorMessagePipe implements PipeTransform {
  private readonly backendFallback: Record<string, string> = {
    [BackendErrorCode.EMAIL_NOT_FOUND]: 'Email address not found.',
    [BackendErrorCode.INCORRECT_PASSWORD]: 'Your password is incorrect.',
    [BackendErrorCode.MFA_CODE_ERROR]: 'The verification code is incorrect or expired.',
    [BackendErrorCode.FORGOT_SUCCESS]: 'Password reset email sent.',
  };

  /**
   * Returns the user-facing message for a code (pipe transform).
   */
  transform(code: string | null | undefined): string {
    if (!code) {
      return '';
    }
    const normalized = this.normalizeCode(code);
    if (normalized in NHS_ERROR_MESSAGES) {
      return NHS_ERROR_MESSAGES[normalized];
    }
    if (normalized in this.backendFallback) {
      return this.backendFallback[normalized];
    }
    if (normalized === 'organisation_offboarded') {
      return 'Your organisation is no longer supported.';
    }
    return normalized;
  }

  /**
   * Normalizes backend aliases (e.g. `cannotLogin`) to app error codes.
   */
  normalizeCode(code: string | null | undefined): string {
    if (!code) {
      return NhsErrorCode.CANNOT_LOGIN;
    }
    if (code in NHS_ERROR_MESSAGES) {
      return code;
    }
    if (code === 'accountMismatch') {
      return 'nhs_account_mismatch';
    }
    const lower = code.toLowerCase();
    if (lower === 'cannotlogin') {
      return NhsErrorCode.CANNOT_LOGIN;
    }
    if (lower in NHS_ERROR_MESSAGES) {
      return lower;
    }
    return code;
  }

  /**
   * Hardcoded title + message for login/guest banners when ErrorHandlingService is not used.
   */
  resolveFromCode(code: string | null | undefined): NhsErrorCopy {
    const normalized = this.normalizeCode(code);
    const message = this.transform(normalized);
    const title = NHS_ERROR_TITLES[normalized] ?? 'Something went wrong';
    return { title, message };
  }
}
